import { getSessionToken } from './monday';
import type {
    Me,
    CoordinatorRow,
    SchedulerRow,
    Comm,
    LtdSuggestion,
    ClaimHolder,
    CommType,
    ScheduleStatus,
    LeadTimeDriverType,
} from '../types';

/** Parsed error body. Callers branch on `code` (the 409 DPAS_OVERRIDE_REQUIRED
 *  guard) or read `held_by` (the 409 claim-conflict). */
export interface ApiErrorBody {
    error?: string;
    code?: string;
    detail?: string;
    ok?: boolean;
    held_by?: { user_id?: number; user_name?: string };
}

/** Thrown for any non-2xx response; carries the parsed body. */
export class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly body: ApiErrorBody | null,
    ) {
        super(body?.error ?? `HTTP ${status}`);
        this.name = 'ApiError';
    }
}

export function asApiError(e: unknown): ApiError | null {
    return e instanceof ApiError ? e : null;
}

// Dev-only identity for the RoleSwitcher. In dev the Worker (ENVIRONMENT=development)
// accepts a spoofable X-Monday-User-Id header; in prod we always use the real
// monday session token. The header is NEVER sent in a production build.
let devUserId: string | null = null;
export function setDevUser(mondayUserId: string | null): void {
    devUserId = mondayUserId;
}

async function authHeaders(): Promise<Record<string, string>> {
    if (import.meta.env.DEV && devUserId) {
        return { 'X-Monday-User-Id': devUserId };
    }
    return { Authorization: `Bearer ${await getSessionToken()}` };
}

// Base URL for the Worker API. Empty in dev (Vite proxies /api → :8787, same
// origin); in the Pages production build set VITE_API_BASE to the Worker URL so the
// embedded app calls it cross-origin (CORS allows the Pages origin via APP_ORIGIN).
const API_BASE = import.meta.env.VITE_API_BASE ?? '';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const res = await fetch(`${API_BASE}${path}`, {
        ...init,
        headers: {
            'Content-Type': 'application/json',
            ...(await authHeaders()),
            ...(init.headers as Record<string, string> | undefined),
        },
    });
    const body =
        res.status === 204 ? null : await (res.json() as Promise<unknown>).catch(() => null);
    if (!res.ok) {
        throw new ApiError(res.status, body as ApiError['body']);
    }
    return body as T;
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
        method: 'POST',
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

export interface CoordinatorUpdate {
    real_need_date?: string;
    real_need_qty?: number;
    customer_expedite_flag?: boolean;
    customer_expedite_reason?: string;
    notes?: string;
}

export const api = {
    me: () => request<Me>('/api/me'),

    // Coordinator
    coordinatorQueue: () => request<{ rows: CoordinatorRow[] }>('/api/coordinator/queue'),
    claim: (lineId: number) =>
        post<{ ok: boolean; claim?: ClaimHolder; held_by?: ClaimHolder }>(
            `/api/coordinator/po/${lineId}/claim`,
        ),
    release: (lineId: number) => post<{ ok: boolean }>(`/api/coordinator/po/${lineId}/release`),
    contact: (lineId: number) => post<{ ok: boolean }>(`/api/coordinator/po/${lineId}/contact`),
    update: (lineId: number, body: CoordinatorUpdate) =>
        post<{ ok: boolean }>(`/api/coordinator/po/${lineId}/update`, body),
    handOff: (lineId: number) => post<{ ok: boolean }>(`/api/coordinator/po/${lineId}/hand-off`),

    // Scheduler
    schedulerQueue: () => request<{ rows: SchedulerRow[] }>('/api/scheduler/queue'),
    promisedDate: (lineId: number, promised_date: string, override_justification?: string) =>
        post<{ ok: boolean; current_promised_date: string; override_recorded: boolean }>(
            `/api/scheduler/po/${lineId}/promised-date`,
            { promised_date, override_justification },
        ),
    // Body shapes match worker/routes/scheduler.ts exactly: expedite reads `flag`/
    // `reason`, status reads `status`.
    expedite: (lineId: number, flag: boolean, reason?: string) =>
        post<{ ok: boolean }>(`/api/scheduler/po/${lineId}/expedite`, {
            flag,
            ...(reason !== undefined ? { reason } : {}),
        }),
    setStatus: (lineId: number, status: ScheduleStatus) =>
        post<{ ok: boolean }>(`/api/scheduler/po/${lineId}/status`, { status }),
    setLeadTimeDriver: (lineId: number, desc: string, type: LeadTimeDriverType) =>
        post<{ ok: boolean }>(`/api/scheduler/po/${lineId}/lead-time-driver`, { desc, type }),
    suggestedLtd: (lineId: number) =>
        request<{ suggestion: LtdSuggestion | null; reason?: string }>(
            `/api/scheduler/po/${lineId}/suggested-lead-time-driver`,
        ),

    // Comms (shared)
    comms: (lineId: number) => request<{ rows: Comm[] }>(`/api/comms/${lineId}`),
    addComm: (lineId: number, comm_type: CommType, summary: string) =>
        post<{ ok: boolean }>(`/api/comms/${lineId}`, { comm_type, summary }),
};
